import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IconSvg from 'src/components/common/ui/IconSvg';

const ContactUs: React.FC = () => {
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Contact form state
    const [email, setEmail] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState<string | null>(null);

    const mobileMenuRef = useRef<HTMLDivElement>(null);

    // Handle mobile menu behavior
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
                setIsMobileMenuOpen(false);
            }
        };

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsMobileMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscapeKey);

        document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'auto';

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
            document.body.style.overflow = 'auto';
        };
    }, [isMobileMenuOpen]);

    // Handle form submit
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!email || !subject || !message) {
            setStatus('Please fill in all required fields.');
            return;
        }

        // 🚀 Replace this with your API call (e.g., send email or save in DB)
        console.log('Form submitted:', { email, subject, message });

        setStatus('Message sent successfully ✅');
        setEmail('');
        setSubject('');
        setMessage('');
    };

    return (
        <>
            {/* Header */}
            <div className="lg:w-[1317px] lg:mx-auto lg:pt-10 pt-5 lg:pb-20 pb-0 lg:px-0 px-4">
                <div className="flex items-center justify-between relative">
                    <IconSvg
                        icon="appLogo"
                        className="cursor-pointer lg:h-auto h-8"
                        onClick={() => navigate('/')}
                    />

                    {/* Mobile Menu Button */}
                    <div
                        className="lg:hidden block cursor-pointer z-50"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    >
                        {isMobileMenuOpen ? (
                            <></>
                        ) : (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="33"
                                viewBox="0 0 32 33"
                                fill="none"
                                className="transition-transform duration-300"
                            >
                                <path
                                    d="M4 23.1666H28M4 16.5H28M4 9.83331H28"
                                    stroke="black"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        )}
                    </div>

                    {/* Desktop Menu */}
                    <div className="lg:flex hidden items-center space-x-32">
                        <div className="flex items-center space-x-20">
                            <p className="text-[22px] font-normal text-grayscale-800 cursor-pointer font-inter">
                                About
                            </p>
                            <p className="text-[22px] font-normal text-grayscale-800 cursor-pointer font-inter">
                                Testimonial
                            </p>
                            <p className="text-[22px] font-normal text-grayscale-800 cursor-pointer font-inter"onClick={() => navigate('/contact-us')}>
                                Contact
                            </p>
                        </div>
                        <div className="flex items-center space-x-4">
                            <button
                                type="button"
                                onClick={() => navigate('/signin')}
                                className="px-6 py-3 rounded-lg cursor-pointer text-grayscale-100 bg-black text-lg font-medium font-inter w-[154px]"
                            >
                                Sign in
                            </button>
                            <button
                                onClick={() => navigate('/signup')}
                                type="button"
                                className="px-6 py-3 rounded-lg cursor-pointer text-grayscale-100 bg-[#6277FF] text-lg font-medium font-inter w-[154px]"
                            >
                                Sign up
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Contact Form Section */}
            <div className="lg:w-[700px] mx-auto text-center py-12 px-4">
                <h2 className="text-3xl font-bold mb-2 " style={{ fontSize: '36px' }}>Contact Us</h2>
                <p className="text-gray-600 mb-8">
                    Got a question? Drop us a message and we&apos;ll get back to you soon.
                </p>


                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="text-left">
                        <label className="block text-gray-800 font-medium mb-2">
                            Email <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="email"
                            placeholder="Enter email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6277FF]"
                        />
                    </div>

                    <div className="text-left">
                        <label className="block text-gray-800 font-medium mb-2">
                            Subject <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="Enter subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6277FF]"
                        />
                    </div>

                    <div className="text-left">
                        <label className="block text-gray-800 font-medium mb-2">
                            Message <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            placeholder="Type message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className="w-full px-4 py-3 border rounded-lg h-32 focus:outline-none focus:ring-2 focus:ring-[#6277FF]"
                        ></textarea>
                    </div>

                    {status && <p className="text-center text-sm text-green-600">{status}</p>}

                    <button
                        type="submit"
                        className="w-full py-3 bg-[#6277FF] text-white rounded-lg font-medium hover:bg-[#4b5fd9] transition"
                    >
                        Send
                    </button>
                </form>
            </div>

            {/* Footer */}
            <div
                className="lg:h-[100px] bg-cover bg-center"
                style={{ backgroundImage: "url('/assets/images/footer.png')" }}
            >
                <div className="lg:w-[1317px] lg:mx-auto flex items-center justify-between py-6 lg:px-0 px-4">
                    {/* Logo */}
                    <img
                        src="/assets/images/footer-logo.png"
                        onClick={() => navigate('/')}
                        alt="Logo"
                        className="lg:h-12 h-8 cursor-pointer"
                    />

                    {/* Footer Nav */}
                    <div className="flex items-center lg:space-x-16 space-x-8">
                        <p className="text-white lg:text-[22px] text-sm font-normal cursor-pointer font-inter">
                            About
                        </p>
                        <p className="text-white lg:text-[22px] text-sm font-normal cursor-pointer font-inter">
                            Testimonial
                        </p>
                        <p className="text-white lg:text-[22px] text-sm font-normal cursor-pointer font-inter"onClick={() => navigate('/contact-us')}>
                            Contact
                        </p>
                        <p
                            className="text-white lg:text-[22px] text-sm font-normal cursor-pointer font-inter"
                            onClick={() => navigate('/privacy-policy')}
                        >
                            Privacy Policy
                        </p>
                    </div>

                    <div className="lg:block hidden">
                        <button
                            type="button"
                            onClick={() => navigate('/signin')}
                            className="px-6 py-2 rounded-lg bg-white text-black text-[18px] font-medium font-inter shadow-md hover:bg-gray-200 transition"
                        >
                            Sign In
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-center py-4 space-x-2">
                <p className="font-inter lg:text-lg text-sm lg:font-medium font-normal text-black">
                    Copyright 2024
                </p>
                <IconSvg icon="copyright" />
            </div>
        </>
    );
};

export default ContactUs;
